import { useState } from 'react';

import { createFeatureGroup, updateFeatureGroup } from '../../api/business/endpoints';
import type { FeatureGroup, TextChanges } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { LocalisedTextField } from '../../components/LocalisedTextField';
import { textChanges, textsOf } from '../../components/localisedText';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useT } from '../../i18n/LanguageContext';

type FeatureGroupDialogProps = {
  companyId: string;
  /** null creates a group; a group renames it. */
  group: FeatureGroup | null;
  onClose: () => void;
};

/**
 * A feature group's name in every content language — unique in the company per language, in any case; the API
 * says so when it is not.
 */
export const FeatureGroupDialog = ({ companyId, group, onClose }: FeatureGroupDialogProps) => {
  const t = useT();
  const { languages, available } = useContentLanguages();
  const original = group ? textsOf(group.translations, 'name', languages) : null;
  const [names, setNames] = useState<Record<string, string>>(original ?? {});

  const mutation = useAppMutation(
    (changes: TextChanges) =>
      group
        ? updateFeatureGroup(companyId, group.id, { names: changes })
        : createFeatureGroup(companyId, changes),
    ['feature-groups', 'product'],
    onClose,
  );

  return (
    <FormDialog
      title={group ? t('features.rename') : t('features.create')}
      onClose={onClose}
      onSubmit={() => mutation.mutate(textChanges(names, original, languages))}
      submitLabel={group ? t('common.save') : t('common.create')}
      isBusy={mutation.isPending || (names[languages[0]] ?? '').trim() === ''}
      error={mutation.error}
      maxWidth="xs"
    >
      <LocalisedTextField
        autoFocus
        label={t('features.name')}
        languages={languages}
        available={available}
        values={names}
        onChange={setNames}
        maxLength={80}
      />
    </FormDialog>
  );
};
